import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getApiUrl } from '../config/api';
import GlobalNav from '../components/GlobalNav';
import { normalizeSlug, getAvailability } from '../utils/availability';
import { publicProfileUrl } from '../utils/profileSharing';
import LinkedInShare from '../components/LinkedInShare';
const G = '#004D35';
const MINT = '#a8f0c6';
const BG = '#f5f3ef';
const BORDER = '#e8e5e0';
const DARK = '#0a1a0f';
const YELLOW = '#f5a623';

function resolvePhotoSrc(photoUrl) {
  if (!photoUrl) return null;
  if (photoUrl.startsWith('http')) return photoUrl;
  if (photoUrl.startsWith('/api/photos/')) return photoUrl;
  const stripped = photoUrl.replace(/^\/?(api\/upload\/?)+/, '');
  return `/api/upload/${stripped}`;
}

function safeCalendarUrl(value) {
  try {
    const url = new URL(value);
    return ['https:', 'http:'].includes(url.protocol) ? url.href : '';
  } catch (_) { return ''; }
}

function Chip({ children, green }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 12px', borderRadius: 999, fontSize: 12, fontWeight: 600, background: green ? 'rgba(0,77,53,0.08)' : '#f0ede8', color: green ? G : '#555' }}>
      {children}
    </span>
  );
}

function IconMapPin() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#666" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
      <path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0 1 18 0z" />
      <circle cx="12" cy="10" r="3" />
    </svg>
  );
}

function IconMonitor() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#666" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
      <rect x="2" y="3" width="20" height="14" rx="2" />
      <line x1="8" y1="21" x2="16" y2="21" />
      <line x1="12" y1="17" x2="12" y2="21" />
    </svg>
  );
}

function IconBriefcase() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#666" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
      <rect x="2" y="7" width="20" height="14" rx="2" />
      <path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" />
    </svg>
  );
}

function GreenDot() {
  return <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#22c55e', display: 'inline-block', flexShrink: 0 }} />;
}


// ── Turnstile widget ──────────────────────────────────────────────────────────
// Renders the Cloudflare Turnstile challenge widget.
// When REACT_APP_TURNSTILE_SITE_KEY is unset (dev), renders nothing —
// the server will log a bypass warning and allow the request through.
function TurnstileWidget({ onToken, onExpire }) {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;

  useEffect(() => {
    if (!SITE_KEY || !containerRef.current) return;

    const renderWidget = () => {
      if (window.turnstile && containerRef.current && widgetIdRef.current === null) {
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: SITE_KEY,
          callback: (token) => onToken && onToken(token),
          'expired-callback': () => onExpire && onExpire(),
          'error-callback': () => onExpire && onExpire(),
        });
      }
    };

    if (window.turnstile) {
      renderWidget();
    } else if (!document.getElementById('cf-turnstile-script')) {
      const script = document.createElement('script');
      script.id = 'cf-turnstile-script';
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
      script.async = true;
      script.defer = true;
      script.onload = renderWidget;
      document.head.appendChild(script);
    } else {
      const iv = setInterval(() => {
        if (window.turnstile) { clearInterval(iv); renderWidget(); }
      }, 80);
      return () => clearInterval(iv);
    }

    return () => {
      if (window.turnstile && widgetIdRef.current !== null) {
        try { window.turnstile.remove(widgetIdRef.current); } catch (_) {}
        widgetIdRef.current = null;
      }
    };
  }, [SITE_KEY, onToken, onExpire]);

  if (!SITE_KEY) return null;
  return <div ref={containerRef} style={{ marginBottom: 12 }} />;
}

// ── Booking Modal ─────────────────────────────────────────────────────────────
function BookingModal({ advisor, onClose }) {
  const [step, setStep] = useState('capture');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [turnstileToken, setTurnstileToken] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [booked, setBooked] = useState(false);

  const leadRef = useRef(null);
  const bookedRef = useRef(false);

  const calType = advisor.calendar_type || 'other';
  const bookingLink = advisor.booking_link || advisor.calendly_url || '';
  const firstName = advisor.first_name || (advisor.name || '').split(' ')[0] || 'your advisor';

  const handleCapture = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) { setError('Please fill in both fields'); return; }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) { setError('Please enter a valid email address'); return; }
    setSubmitting(true); setError('');
    try {
      const res = await fetch('/api/leads', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          advisor_id: advisor.id,
          business_owner_name: name.trim(),
          business_owner_email: email.trim(),
          source: 'booking',
          cf_turnstile_token: turnstileToken,
        }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Something went wrong — please try again'); return; }
      leadRef.current = { id: data.lead.id, action_token: data.lead.action_token };
      setStep('calendar');
    } catch {
      setError('Network error — please try again');
    } finally {
      setSubmitting(false);
    }
  };

  const markBooked = useCallback(async (meetingStartAt) => {
    if (bookedRef.current || !leadRef.current) return;
    bookedRef.current = true;
    setBooked(true);
    const { id, action_token } = leadRef.current;
    try {
      await fetch(`/api/leads/${id}/booked`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action_token, meeting_start_at: meetingStartAt || null }),
      });
    } catch (err) {
      console.error('[BookingModal] Failed to mark lead as booked:', err);
    }
  }, []);

  useEffect(() => {
    if (step !== 'calendar' || calType !== 'calendly' || !bookingLink) return;
    const prefillUrl = bookingLink.includes('?')
      ? `${bookingLink}&name=${encodeURIComponent(name)}&email=${encodeURIComponent(email)}`
      : `${bookingLink}?name=${encodeURIComponent(name)}&email=${encodeURIComponent(email)}`;
    const init = () => {
      const container = document.getElementById('booking-calendly-embed');
      if (container && window.Calendly?.initInlineWidget) {
        window.Calendly.initInlineWidget({ url: prefillUrl, parentElement: container, prefill: { name: name.trim(), email: email.trim() } });
      }
    };
    if (window.Calendly?.initInlineWidget) { init(); }
    else {
      const script = document.createElement('script');
      script.src = 'https://assets.calendly.com/assets/external/widget.js';
      script.async = true; script.onload = init;
      document.head.appendChild(script);
    }
  }, [step]);

  useEffect(() => {
    if (step !== 'calendar' || calType !== 'calendly') return;
    const handler = (e) => {
      if (e.data?.event === 'calendly.event_scheduled') markBooked(e.data?.payload?.event?.start_time || null);
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [step, calType, markBooked]);

  useEffect(() => {
    if (step !== 'calendar' || calType !== 'cal_com') return;
    const handler = (e) => {
      if (e.data?.type === 'cal:booking-successful') markBooked(e.data?.data?.date || null);
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [step, calType, markBooked]);

  const isEmbedCalendar = (calType === 'calendly' || calType === 'cal_com') && step === 'calendar' && !booked;
  const modalWidth = isEmbedCalendar ? 'min(720px, 96vw)' : 'min(460px, 96vw)';
  const calPrefillUrl = bookingLink.includes('?')
    ? `${bookingLink}&name=${encodeURIComponent(name)}&email=${encodeURIComponent(email)}&embed=true`
    : `${bookingLink}?name=${encodeURIComponent(name)}&email=${encodeURIComponent(email)}&embed=true`;

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(10,26,15,0.52)', backdropFilter: 'blur(4px)', zIndex: 900 }} />
      <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', background: '#fff', borderRadius: 20, padding: isEmbedCalendar ? '24px 24px 0' : '32px', width: modalWidth, maxHeight: '92vh', overflowY: isEmbedCalendar ? 'hidden' : 'auto', zIndex: 901, boxShadow: '0 24px 80px rgba(0,0,0,0.22)', display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: isEmbedCalendar ? 14 : 24 }}>
          <div>
            <h3 style={{ fontFamily: 'Georgia, serif', fontSize: 20, margin: 0, color: DARK }}>
              {booked ? 'You\'re booked!' : step === 'capture' ? 'Book a free intro call' : `Pick a time with ${firstName}`}
            </h3>
            {step === 'capture' && <p style={{ margin: '5px 0 0', fontSize: 13, color: '#888' }}>15 minutes · no obligation</p>}
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 22, lineHeight: 1, cursor: 'pointer', color: '#bbb', padding: '0 0 0 16px', marginTop: -2 }}>×</button>
        </div>

        {step === 'capture' && (
          <form onSubmit={handleCapture}>
            <p style={{ margin: '0 0 22px', fontSize: 14, color: '#666', lineHeight: 1.5 }}>So {firstName} knows who's coming.</p>
            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>Your name</label>
              <input value={name} onChange={e => setName(e.target.value)} placeholder="Jane Smith" autoFocus style={{ width: '100%', padding: '11px 14px', border: `1.5px solid ${BORDER}`, borderRadius: 10, fontSize: 14, outline: 'none', boxSizing: 'border-box', color: DARK }} />
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>Email</label>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="jane@yourbusiness.com" style={{ width: '100%', padding: '11px 14px', border: `1.5px solid ${BORDER}`, borderRadius: 10, fontSize: 14, outline: 'none', boxSizing: 'border-box', color: DARK }} />
            </div>
            <TurnstileWidget
              onToken={setTurnstileToken}
              onExpire={() => setTurnstileToken('')}
            />
            {error && <div role="alert" style={{ marginBottom: 16, padding: '10px 14px', background: '#fdf2f2', border: '1px solid #f5c6c6', borderRadius: 9, fontSize: 13, color: '#9f3023' }}>{error}{advisor.email && error.includes('Please email') && <> <a href={`mailto:${advisor.email}`} style={{ color: G, fontWeight: 700 }}>Email {advisor.email}</a></>}</div>}
            <button type="submit" disabled={submitting} style={{ width: '100%', background: G, color: '#fff', border: 'none', padding: '14px 0', borderRadius: 999, fontSize: 15, fontWeight: 700, cursor: submitting ? 'default' : 'pointer', opacity: submitting ? 0.7 : 1 }}>
              {submitting ? 'One moment…' : 'Continue to booking →'}
            </button>
          </form>
        )}

        {step === 'calendar' && calType === 'calendly' && !booked && (
          <div id="booking-calendly-embed" style={{ flex: 1, minHeight: 620, borderRadius: '0 0 20px 20px', overflow: 'hidden' }} />
        )}
        {step === 'calendar' && calType === 'cal_com' && !booked && (
          <div style={{ flex: 1, borderRadius: '0 0 20px 20px', overflow: 'hidden' }}>
            <iframe src={calPrefillUrl} style={{ width: '100%', height: '620px', border: 'none', display: 'block' }} allow="payment" title={`Book a time with ${firstName}`} />
          </div>
        )}
        {step === 'calendar' && (calType === 'google_calendar' || calType === 'other') && !booked && (
          <div style={{ textAlign: 'center', padding: '20px 0 32px' }}>
            <div style={{ fontSize: 44, marginBottom: 18 }}>📅</div>
            <p style={{ margin: '0 0 6px', fontSize: 15, fontWeight: 600, color: DARK }}>You're all set — open {firstName}'s calendar to pick a time.</p>
            <p style={{ margin: '0 0 28px', fontSize: 13, color: '#888', lineHeight: 1.5 }}>{firstName} will be notified and is expecting you.</p>
            <a href={bookingLink} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-block', background: G, color: '#fff', padding: '13px 30px', borderRadius: 999, fontSize: 15, fontWeight: 700, textDecoration: 'none' }}>
              Open {firstName}'s calendar →
            </a>
          </div>
        )}
        {step === 'calendar' && booked && (
          <div style={{ textAlign: 'center', padding: '20px 0 32px' }}>
            <div style={{ fontSize: 52, marginBottom: 16 }}>🎉</div>
            <p style={{ margin: '0 0 8px', fontSize: 16, fontWeight: 700, color: DARK }}>Your intro call with {firstName} is confirmed.</p>
            <p style={{ margin: '0 0 28px', fontSize: 14, color: '#888' }}>Check your email for a calendar invite.</p>
            <button onClick={onClose} style={{ background: G, color: '#fff', border: 'none', padding: '12px 30px', borderRadius: 999, fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>Done</button>
          </div>
        )}
      </div>
    </>
  );
}

// ── Message Modal ─────────────────────────────────────────────────────────────
function MessageModal({ advisor, onClose }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [turnstileToken, setTurnstileToken] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const attemptKey = useRef(null);

  const firstName = advisor.first_name || (advisor.name || '').split(' ')[0] || 'your advisor';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !message.trim()) { setError('Please fill in all three fields'); return; }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) { setError('Please enter a valid email address'); return; }
    setSubmitting(true); setError('');
    if (!attemptKey.current) {
      attemptKey.current = window.crypto?.randomUUID
        ? window.crypto.randomUUID()
        : `msg-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    }
    try {
      const res = await fetch('/api/leads', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Idempotency-Key': attemptKey.current },
        body: JSON.stringify({
          advisor_id: advisor.id,
          business_owner_name: name.trim(),
          business_owner_email: email.trim(),
          message: message.trim(),
          source: 'message',
          cf_turnstile_token: turnstileToken,
        }),
      });
      const data = await res.json();
      if (!res.ok || data.success !== true) {
        if (res.status === 502) setError(`Message delivery is temporarily unavailable. Please email ${advisor.email || 'the advisor'} directly instead.`);
        else setError(data.error || 'Your message could not be sent. Please try again.');
        return;
      }
      setSubmitted(true);
    } catch {
      setError('Network error — please try again');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(10,26,15,0.52)', backdropFilter: 'blur(4px)', zIndex: 900 }} />
      <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', background: '#fff', borderRadius: 20, padding: '32px', width: 'min(480px, 96vw)', maxHeight: '92vh', overflowY: 'auto', zIndex: 901, boxShadow: '0 24px 80px rgba(0,0,0,0.22)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: submitted ? 20 : 24 }}>
          <h3 style={{ fontFamily: 'Georgia, serif', fontSize: 20, margin: 0, color: DARK }}>
            {submitted ? 'Message sent!' : `Send ${firstName} a message`}
          </h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 22, lineHeight: 1, cursor: 'pointer', color: '#bbb', padding: '0 0 0 16px', marginTop: -2 }}>×</button>
        </div>

        {submitted ? (
          <div style={{ textAlign: 'center', padding: '8px 0 8px' }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>✉️</div>
            <p style={{ margin: '0 0 8px', fontSize: 15, color: DARK, fontWeight: 600 }}>Your message is on its way.</p>
            <p style={{ margin: '0 0 28px', fontSize: 14, color: '#888', lineHeight: 1.65 }}>
              Your message was submitted to the advisor’s email service. They can reply directly to your email address. Response times vary.
            </p>
            <button onClick={onClose} style={{ background: G, color: '#fff', border: 'none', padding: '12px 30px', borderRadius: 999, fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>Done</button>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>Your name</label>
              <input value={name} onChange={e => { setName(e.target.value); attemptKey.current = null; }} placeholder="Jane Smith" autoFocus style={{ width: '100%', padding: '11px 14px', border: `1.5px solid ${BORDER}`, borderRadius: 10, fontSize: 14, outline: 'none', boxSizing: 'border-box', color: DARK }} />
            </div>
            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>Email</label>
              <input type="email" value={email} onChange={e => { setEmail(e.target.value); attemptKey.current = null; }} placeholder="jane@yourbusiness.com" style={{ width: '100%', padding: '11px 14px', border: `1.5px solid ${BORDER}`, borderRadius: 10, fontSize: 14, outline: 'none', boxSizing: 'border-box', color: DARK }} />
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>Message</label>
              <textarea
                value={message} onChange={e => { setMessage(e.target.value); attemptKey.current = null; }}
                rows={5} maxLength={2000}
                placeholder={`Hi ${firstName}, I'd love to connect about…`}
                style={{ width: '100%', padding: '11px 14px', border: `1.5px solid ${BORDER}`, borderRadius: 10, fontSize: 14, outline: 'none', resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.6, boxSizing: 'border-box', color: DARK }}
              />
              <div style={{ textAlign: 'right', fontSize: 11, color: '#ccc', marginTop: 3 }}>{message.length}/2000</div>
            </div>
            {advisor.email && <p style={{ margin: '-4px 0 16px', color: '#6d786f', fontSize: 12, lineHeight: 1.5 }}>Prefer email? <a href={`mailto:${advisor.email}`} style={{ color: G, fontWeight: 700 }}>{advisor.email}</a></p>}
            <TurnstileWidget
              onToken={setTurnstileToken}
              onExpire={() => setTurnstileToken('')}
            />
            {error && <div role="alert" style={{ marginBottom: 16, padding: '10px 14px', background: '#fdf2f2', border: '1px solid #f5c6c6', borderRadius: 9, fontSize: 13, color: '#9f3023' }}>{error}{advisor.email && error.includes('Please email') && <> <a href={`mailto:${advisor.email}`} style={{ color: G, fontWeight: 700 }}>Email {advisor.email}</a></>}</div>}
            <button type="submit" disabled={submitting} style={{ width: '100%', background: G, color: '#fff', border: 'none', padding: '14px 0', borderRadius: 999, fontSize: 15, fontWeight: 700, cursor: submitting ? 'default' : 'pointer', opacity: submitting ? 0.7 : 1, transition: 'opacity 0.15s' }}>
              {submitting ? 'Sending…' : 'Send message'}
            </button>
          </form>
        )}
      </div>
    </>
  );
}

// ── Main profile page ─────────────────────────────────────────────────────────
const AdvisorProfile = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, getAuthHeader, isDev } = useAuth();

  const [advisor, setAdvisor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [toast, setToast] = useState('');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 900);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [testimonials, setTestimonials] = useState([]);

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 2500); };

  useEffect(() => {
    const h = () => setIsMobile(window.innerWidth < 900);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);

  useEffect(() => {
    const load = async () => {
      setLoading(true); setNotFound(false); setAdvisor(null);
      try {
        const isNumericId = /^\d+$/.test(slug);
        const advUrl = isNumericId ? getApiUrl(`/api/advisors/${slug}`) : getApiUrl(`/api/advisors/by-slug/${slug}`);
        const advRes = await fetch(advUrl, { credentials: 'include' });
        if (advRes.status === 404) { setNotFound(true); setLoading(false); return; }
        if (!advRes.ok) { setNotFound(true); setLoading(false); return; }
        const advData = await advRes.json();
        // Numeric ID → redirect to canonical slug URL
        if (isNumericId && advData.profile_slug) { navigate(`/advisor/${advData.profile_slug}`, { replace: true }); return; }
        // Stale slug → by-slug returned a redirect hint from slug_history
        if (!isNumericId && advData.redirectSlug) { navigate(`/advisor/${advData.redirectSlug}`, { replace: true }); return; }
        setAdvisor(advData);
      } catch (e) { console.error(e); setNotFound(true); }
      finally { setLoading(false); }
    };
    load();
  }, [slug, user, navigate]);

  useEffect(() => {
    if (!advisor?.id) return;
    fetch(`/api/testimonials/public/${advisor.id}`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d?.testimonials) setTestimonials(d.testimonials); })
      .catch(() => {});
  }, [advisor?.id]);

  useEffect(() => {
    if (!advisor?.id || isDev) return;
    fetch(getApiUrl(`/api/advisor/${advisor.id}/view`), {
      method: 'POST', credentials: 'include',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ source: 'browse' }),
    }).catch(() => {});
  }, [advisor?.id, isDev]);

  if (loading) return (
    <div style={{ minHeight: '100vh', background: BG }}>
      <GlobalNav />
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
        <div style={{ width: 40, height: 40, border: `3px solid ${BORDER}`, borderTopColor: G, borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      </div>
    </div>
  );

  if (notFound || (!loading && !advisor)) return (
    <div style={{ minHeight: '100vh', background: BG }}>
      <GlobalNav />
      <div style={{ maxWidth: 560, margin: '0 auto', padding: '100px 32px', textAlign: 'center' }}>
        <div style={{ width: 64, height: 64, borderRadius: '50%', background: '#f0ede8', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px', fontSize: 28 }}>🔍</div>
        <h2 style={{ fontFamily: 'Georgia, serif', fontSize: 26, color: DARK, margin: '0 0 12px' }}>This profile isn't available</h2>
        <p style={{ color: '#888', fontSize: 15, lineHeight: 1.6, margin: '0 0 32px' }}>This advisor may have removed their profile or it's temporarily unavailable.</p>
        <button onClick={() => navigate('/browse')} style={{ background: G, color: '#fff', border: 'none', padding: '13px 28px', borderRadius: 999, fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>Browse advisors →</button>
      </div>
    </div>
  );

  const vis = advisor.visibility || { about: true, why: true, challenges: true, hobbies: true, contact: true, specialties: true, pricing: true };
  const parseArr = (v) => { if (Array.isArray(v)) return v; try { return JSON.parse(v || '[]'); } catch { return []; } };
  const challenges = parseArr(advisor.client_challenges);
  const hobbies = parseArr(advisor.hobbies);
  const specialties = parseArr(advisor.specialties);
  const industries = parseArr(advisor.industries_served);
  const goodFitFor = parseArr(advisor.good_fit_for);
  const businessSizes = parseArr(advisor.business_sizes);
  const licensedStates = parseArr(advisor.licensed_states);
  const showLicensedStates = (advisor.category_slug === 'cpa' || advisor.category_slug === 'insurance');
  const licensedStatesText = licensedStates.includes('all') ? 'All states' : licensedStates.join(', ');
  const name = [advisor.first_name, advisor.last_name].filter(Boolean).join(' ') || advisor.name || 'Advisor';
  const initials = name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  const hasBooking = !!(advisor.booking_link || advisor.calendly_url);
  const calendarUrl = safeCalendarUrl(advisor.booking_link || advisor.calendly_url);
  const profileUrl = publicProfileUrl(advisor.profile_slug || advisor.id);
  const shareProfile = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: `${name} | Viysr`, url: profileUrl }); return; }
      catch (e) { if (e.name === 'AbortError') return; }
    }
    try { await navigator.clipboard.writeText(profileUrl); showToast('Profile link copied'); }
    catch (_) { showToast(`Copy this profile URL: ${profileUrl}`); }
  };

  const SectionCard = ({ title, visible = true, children }) => {
    if (!visible) return null;
    return (
      <div style={{ background: '#fff', border: `1.5px solid ${BORDER}`, borderRadius: 18, padding: '28px 32px', marginBottom: 16 }}>
        {title && <h2 style={{ fontSize: 18, fontWeight: 700, color: DARK, fontFamily: 'Georgia, serif', margin: '0 0 18px' }}>{title}</h2>}
        {children}
      </div>
    );
  };

  const quickFacts = [
    { label: 'Category', value: advisor.primary_category },
    { label: 'Experience', value: advisor.years_experience },
    vis.pricing !== false && { label: 'Rate', value: advisor.hourly_rate ? `$${advisor.hourly_rate}/hr` : null },
    vis.pricing !== false && { label: 'Pricing', value: advisor.pricing_type },
    { label: 'Free consult', value: advisor.free_consultation ? (advisor.consultation_length ? `Yes · ${advisor.consultation_length}` : 'Yes') : null },
    { label: 'Availability', value: advisor.availability_status ? getAvailability(normalizeSlug(advisor.availability_status)).label : null },
    { label: 'Location', value: advisor.location },
    { label: 'Remote', value: advisor.remote_available ? 'Available' : null },
    showLicensedStates && licensedStates.length > 0 && { label: 'Licensed in', value: licensedStatesText },
  ].filter(Boolean).filter(r => r && r.value);

  return (
    <div style={{ minHeight: '100vh', background: BG, fontFamily: 'system-ui, sans-serif' }}>
      <GlobalNav />

      {toast && (
        <div style={{ position: 'fixed', bottom: isMobile ? 90 : 24, left: '50%', transform: 'translateX(-50%)', background: DARK, color: '#fff', padding: '12px 28px', borderRadius: 999, fontSize: 14, zIndex: 9999, boxShadow: '0 8px 32px rgba(0,0,0,0.2)', whiteSpace: 'nowrap' }}>
          {toast}
        </div>
      )}

      {showMessageModal && advisor && <MessageModal advisor={advisor} onClose={() => setShowMessageModal(false)} />}

      <div style={{ maxWidth: 1060, margin: '0 auto', padding: isMobile ? '40px 24px 140px' : '40px 24px 120px' }}>
        <button onClick={() => navigate('/browse')} style={{ background: 'none', border: 'none', color: '#888', fontSize: 13, cursor: 'pointer', padding: 0, marginBottom: 20, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          ← Back to Browse
        </button>
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 320px', gap: 24, alignItems: 'start' }}>

          {/* LEFT COLUMN */}
          <div>
            {/* HERO */}
            <div style={{ background: '#fff', border: `1.5px solid ${BORDER}`, borderRadius: 18, padding: 32, marginBottom: 16 }}>
              <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                <div style={{ width: 88, height: 88, borderRadius: '50%', background: G, border: `2px solid ${BORDER}`, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', flexShrink: 0 }}>
                  {resolvePhotoSrc(advisor.profile_photo_url) ? (
                    <img src={resolvePhotoSrc(advisor.profile_photo_url)} alt={name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : (
                    <span style={{ fontSize: 30, fontWeight: 700, color: '#fff' }}>{initials}</span>
                  )}
                </div>
                <div style={{ flex: 1, minWidth: 200 }}>
                  <h1 style={{ fontSize: 26, fontWeight: 700, color: DARK, fontFamily: 'Georgia, serif', margin: '0 0 4px' }}>{name}</h1>
                  {advisor.professional_title && <div style={{ fontSize: 15, color: '#666', marginBottom: 4 }}>{advisor.professional_title}</div>}
                  {advisor.primary_category && <span style={{ fontSize: 11, fontFamily: 'sans-serif', fontWeight: 700, color: G, background: 'rgba(0,77,53,0.08)', padding: '3px 10px', borderRadius: 999, textTransform: 'uppercase', letterSpacing: '0.06em', display: 'inline-block', marginBottom: 10 }}>{advisor.primary_category}</span>}
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
                    {advisor.location && <Chip><IconMapPin /> {advisor.location}</Chip>}
                    {showLicensedStates && licensedStates.length > 0 && (
                      <Chip>{licensedStates.includes('all') ? 'Licensed in all states' : `Licensed in: ${licensedStatesText}`}</Chip>
                    )}
                    {advisor.remote_available && <Chip><IconMonitor /> Remote</Chip>}
                    {(() => {
                      const avSlug = normalizeSlug(advisor.availability_status);
                      const avInfo = getAvailability(avSlug);
                      return (
                        <Chip green={avSlug === 'available_now'}>
                          {avSlug === 'available_now' && <GreenDot />}
                          {avInfo.label}
                        </Chip>
                      );
                    })()}
                    {advisor.years_experience && <Chip><IconBriefcase /> {advisor.years_experience} yrs exp</Chip>}
                  </div>
                </div>
              </div>
              {isMobile && (
                <div style={{ marginTop: 20, paddingTop: 20, borderTop: `1px solid ${BORDER}` }}>
                  {hasBooking ? (
                    <>
                      <a href={calendarUrl || undefined} target="_blank" rel="noopener noreferrer" onClick={e => { if (!calendarUrl) e.preventDefault(); }} style={{ display: 'block', boxSizing: 'border-box', width: '100%', textAlign: 'center', background: G, color: '#fff', padding: '14px 0', borderRadius: 999, fontSize: 15, fontWeight: 700, textDecoration: 'none', marginBottom: 10 }}>{calendarUrl ? 'Visit booking calendar' : 'Calendar link unavailable'}</a>
                      <div style={{ textAlign: 'center' }}>
                        <button onClick={() => setShowMessageModal(true)} style={{ background: 'none', border: 'none', color: '#888', fontSize: 13, cursor: 'pointer', padding: 0, textDecoration: 'underline', textUnderlineOffset: 3 }}>
                          Not ready for a call? Send a message.
                        </button>
                      </div>
                    </>
                  ) : (
                    <button onClick={() => setShowMessageModal(true)} style={{ width: '100%', background: G, color: '#fff', border: 'none', padding: '14px 0', borderRadius: 999, fontSize: 15, fontWeight: 700, cursor: 'pointer' }}>
                      Send a message
                    </button>
                  )}
                </div>
              )}
            </div>

            {vis.about !== false && (advisor.bio || advisor.headline) && (
              <SectionCard title="About">
                {advisor.headline && <p style={{ fontSize: 15, fontWeight: 500, color: '#444', lineHeight: 1.7, marginTop: 0, marginBottom: advisor.bio ? 12 : 0, fontStyle: 'italic' }}>{advisor.headline}</p>}
                {advisor.bio && <p style={{ margin: 0, fontSize: 15, color: '#555', lineHeight: 1.75 }}>{advisor.bio}</p>}
              </SectionCard>
            )}

            {vis.why !== false && advisor.why_i_do_this && (
              <SectionCard title="Why I do this work">
                <p style={{ margin: 0, fontSize: 15, color: '#555', lineHeight: 1.75 }}>{advisor.why_i_do_this}</p>
              </SectionCard>
            )}

            {(goodFitFor.length > 0 || businessSizes.length > 0) && (
              <SectionCard title="Who I work with">
                {goodFitFor.length > 0 && (
                  <div style={{ marginBottom: businessSizes.length > 0 ? 18 : 0 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>Good fit for</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                      {goodFitFor.map(g => <span key={g} style={{ background: MINT, color: G, padding: '5px 14px', borderRadius: 999, fontSize: 12, fontWeight: 600 }}>{g}</span>)}
                    </div>
                  </div>
                )}
                {businessSizes.length > 0 && (
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>Business size</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                      {businessSizes.map(s => <span key={s} style={{ background: '#f0ede8', color: '#555', padding: '5px 14px', borderRadius: 999, fontSize: 12, fontWeight: 600 }}>{s}</span>)}
                    </div>
                  </div>
                )}
              </SectionCard>
            )}

            {vis.pricing !== false && (advisor.pricing_note || advisor.hourly_rate || advisor.retainer_rate) && (
              <SectionCard title="Pricing">
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {advisor.hourly_rate && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: `1px solid ${BORDER}` }}>
                      <span style={{ fontSize: 14, color: '#888', fontWeight: 600 }}>Hourly rate</span>
                      <span style={{ fontSize: 15, color: DARK, fontWeight: 700 }}>${advisor.hourly_rate}/hr</span>
                    </div>
                  )}
                  {advisor.retainer_rate && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: advisor.pricing_note ? `1px solid ${BORDER}` : 'none' }}>
                      <span style={{ fontSize: 14, color: '#888', fontWeight: 600 }}>Retainer rate</span>
                      <span style={{ fontSize: 15, color: DARK, fontWeight: 700 }}>${advisor.retainer_rate}/mo</span>
                    </div>
                  )}
                  {advisor.pricing_note && <p style={{ margin: 0, fontSize: 14, color: '#555', lineHeight: 1.65 }}>{advisor.pricing_note}</p>}
                </div>
              </SectionCard>
            )}

            {vis.challenges !== false && challenges.length > 0 && (
              <SectionCard title="Challenges my clients face">
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {challenges.map((c, i) => (
                    <div key={i} style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
                      <div style={{ width: 28, height: 28, borderRadius: '50%', background: MINT, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 }}>
                        <span style={{ fontSize: 11, fontWeight: 800, color: G }}>{i + 1}</span>
                      </div>
                      <p style={{ margin: 0, fontSize: 15, color: '#555', lineHeight: 1.65, paddingTop: 4 }}>{c}</p>
                    </div>
                  ))}
                </div>
              </SectionCard>
            )}

            {vis.specialties !== false && (specialties.length > 0 || industries.length > 0) && (
              <SectionCard title="Specialties & Industries">
                {specialties.length > 0 && (
                  <div style={{ marginBottom: industries.length > 0 ? 18 : 0 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>Specialties</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                      {specialties.includes('All specialties') ? (
                        <span style={{ background: MINT, color: G, padding: '5px 14px', borderRadius: 999, fontSize: 12, fontWeight: 600 }}>Works across all areas</span>
                      ) : specialties.map(s => <span key={s} style={{ background: MINT, color: G, padding: '5px 14px', borderRadius: 999, fontSize: 12, fontWeight: 600 }}>{s}</span>)}
                    </div>
                  </div>
                )}
                {industries.length > 0 && (
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>Industries Served</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                      {industries.includes('All industries') ? (
                        <span style={{ background: MINT, color: G, padding: '5px 14px', borderRadius: 999, fontSize: 12, fontWeight: 600 }}>Open to all industries</span>
                      ) : industries.map(ind => <span key={ind} style={{ background: '#f0ede8', color: '#555', padding: '5px 14px', borderRadius: 999, fontSize: 12, fontWeight: 600 }}>{ind}</span>)}
                    </div>
                  </div>
                )}
              </SectionCard>
            )}

            {testimonials.length > 0 && (
              <SectionCard title="What clients say">
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  {testimonials.map((t, i) => (
                    <div key={t.id} style={{ padding: '16px 0', borderBottom: i < testimonials.length - 1 ? `1px solid ${BORDER}` : 'none' }}>
                      <p style={{ margin: '0 0 10px', fontSize: 15, color: '#555', lineHeight: 1.7, fontStyle: 'italic' }}>"{t.text}"</p>
                      <div style={{ fontSize: 13, fontWeight: 700, color: DARK }}>{t.author_name}</div>
                      {t.company_name && <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>{t.company_name}</div>}
                    </div>
                  ))}
                </div>
              </SectionCard>
            )}

            {vis.hobbies !== false && hobbies.length > 0 && (
              <SectionCard title="Outside the office">
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                  {hobbies.map(h => <span key={h} style={{ background: '#f0ede8', color: '#444', padding: '8px 16px', borderRadius: 999, fontSize: 13, fontWeight: 500 }}>{h}</span>)}
                </div>
              </SectionCard>
            )}

            <SectionCard title="Share this profile">
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                <LinkedInShare profileUrl={profileUrl} />
                <button onClick={shareProfile} style={{ display: 'inline-flex', alignItems: 'center', padding: '9px 18px', borderRadius: 999, border: `1.5px solid ${BORDER}`, fontSize: 13, fontWeight: 600, color: '#444', background: '#fff', cursor: 'pointer' }}>Share / copy</button>
                <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(profileUrl)}`} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', padding: '9px 18px', borderRadius: 999, border: `1.5px solid ${BORDER}`, fontSize: 13, fontWeight: 600, color: '#444', textDecoration: 'none', background: '#fff' }}>Facebook</a>
                <a href={`mailto:?subject=${encodeURIComponent(`Advisor profile: ${name}`)}&body=${encodeURIComponent(profileUrl)}`} style={{ display: 'inline-flex', alignItems: 'center', padding: '9px 18px', borderRadius: 999, border: `1.5px solid ${BORDER}`, fontSize: 13, fontWeight: 600, color: '#444', textDecoration: 'none', background: '#fff' }}>Email</a>
                <a href={`sms:?body=${encodeURIComponent(profileUrl)}`} style={{ display: 'inline-flex', alignItems: 'center', padding: '9px 18px', borderRadius: 999, border: `1.5px solid ${BORDER}`, fontSize: 13, fontWeight: 600, color: '#444', textDecoration: 'none', background: '#fff' }}>Text</a>
                <button
                  onClick={shareProfile}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 18px', borderRadius: 999, border: `1.5px solid ${BORDER}`, fontSize: 13, fontWeight: 600, color: '#444', background: '#fff', cursor: 'pointer' }}
                >
                  Copy link
                </button>
              </div>
              {advisor.email && <p style={{ margin: '16px 0 0', fontSize: 13, color: '#68746c' }}>Contact directly: <a href={`mailto:${advisor.email}`} style={{ color: G, fontWeight: 700 }}>{advisor.email}</a></p>}
            </SectionCard>
          </div>

          {/* RIGHT SIDEBAR */}
          {!isMobile && (
            <div style={{ position: 'sticky', top: 80 }}>

              {/* ── Primary CTA card ── */}
              <div style={{ background: '#fff', border: `1.5px solid ${BORDER}`, borderRadius: 18, padding: '24px', marginBottom: 16 }}>
                {hasBooking ? (
                  <>
                    <a href={calendarUrl || undefined} target="_blank" rel="noopener noreferrer" onClick={e => { if (!calendarUrl) e.preventDefault(); }} style={{ display: 'block', boxSizing: 'border-box', width: '100%', textAlign: 'center', background: G, color: '#fff', padding: '14px 0', borderRadius: 999, fontSize: 15, fontWeight: 700, textDecoration: 'none' }}>{calendarUrl ? 'Visit booking calendar' : 'Calendar link unavailable'}</a>
                    <p style={{ margin: '8px 0 14px', fontSize: 12, color: '#aaa', textAlign: 'center' }}>Choose a time directly on the advisor’s calendar.</p>
                    <div style={{ textAlign: 'center', borderTop: `1px solid ${BORDER}`, paddingTop: 14 }}>
                      <button
                        onClick={() => setShowMessageModal(true)}
                        style={{ background: 'none', border: 'none', color: '#888', fontSize: 13, cursor: 'pointer', padding: 0, textDecoration: 'underline', textUnderlineOffset: 3 }}
                      >
                        Not ready for a call? Send a message.
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <button
                      onClick={() => setShowMessageModal(true)}
                      style={{ width: '100%', background: G, color: '#fff', border: 'none', padding: '14px 0', borderRadius: 999, fontSize: 15, fontWeight: 700, cursor: 'pointer', transition: 'opacity 0.15s' }}
                      onMouseOver={e => e.currentTarget.style.opacity = '0.88'}
                      onMouseOut={e => e.currentTarget.style.opacity = '1'}
                    >
                      Send a message
                    </button>
                    <p style={{ margin: '8px 0 0', fontSize: 12, color: '#aaa', textAlign: 'center' }}>Most advisors respond in 1–2 business days</p>
                  </>
                )}
              </div>

              {/* Social links */}
              {(advisor.linkedin_url || advisor.website_url || advisor.website) && (
              <div style={{ background: '#fff', border: `1.5px solid ${BORDER}`, borderRadius: 18, padding: '28px 24px', marginBottom: 16 }}>
                  <div style={{ display: 'flex', justifyContent: 'center', gap: 16 }}>
                    {advisor.linkedin_url && <a href={advisor.linkedin_url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, color: '#888', textDecoration: 'none', fontWeight: 600 }}>LinkedIn →</a>}
                    {(advisor.website_url || advisor.website) && <a href={advisor.website_url || advisor.website} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, color: '#888', textDecoration: 'none', fontWeight: 600 }}>Website →</a>}
                  </div>
              </div>
              )}

              {/* Quick Facts */}
              {quickFacts.length > 0 && (
                <div style={{ background: '#fff', border: `1.5px solid ${BORDER}`, borderRadius: 18, padding: '22px 24px' }}>
                  <div style={{ fontWeight: 700, fontSize: 13, color: DARK, marginBottom: 14, fontFamily: 'Georgia, serif' }}>Quick Facts</div>
                  {quickFacts.map(({ label, value }) => (
                    <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: `1px solid ${BORDER}` }}>
                      <span style={{ fontSize: 12, color: '#aaa', fontWeight: 600 }}>{label}</span>
                      <span style={{ fontSize: 13, color: DARK, fontWeight: 500, textAlign: 'right', maxWidth: '60%' }}>{value}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ── Mobile sticky bar ── */}
      {isMobile && (
        <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, background: '#fff', borderTop: `1.5px solid ${BORDER}`, padding: '12px 20px 16px', zIndex: 200, boxShadow: '0 -4px 20px rgba(0,0,0,0.06)' }}>
          {hasBooking ? (
            <>
              <a href={calendarUrl || undefined} target="_blank" rel="noopener noreferrer" onClick={e => { if (!calendarUrl) e.preventDefault(); }} style={{ display: 'block', boxSizing: 'border-box', width: '100%', textAlign: 'center', background: G, color: '#fff', padding: '15px 0', borderRadius: 999, fontSize: 15, fontWeight: 700, textDecoration: 'none', marginBottom: 8 }}>{calendarUrl ? 'Visit booking calendar' : 'Calendar link unavailable'}</a>
              <div style={{ textAlign: 'center' }}>
                <button onClick={() => setShowMessageModal(true)} style={{ background: 'none', border: 'none', color: '#888', fontSize: 12, cursor: 'pointer', padding: 0, textDecoration: 'underline', textUnderlineOffset: 3 }}>
                  Not ready for a call? Send a message.
                </button>
              </div>
            </>
          ) : (
            <>
              <button onClick={() => setShowMessageModal(true)} style={{ width: '100%', background: G, color: '#fff', border: 'none', padding: '15px 0', borderRadius: 999, fontSize: 15, fontWeight: 700, cursor: 'pointer' }}>
                Send a message
              </button>
              <p style={{ margin: '6px 0 0', textAlign: 'center', fontSize: 11, color: '#aaa' }}>Most advisors respond in 1–2 business days</p>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default AdvisorProfile;
